import React, { useCallback, useMemo } from 'react';
import Container from 'react-bootstrap/Container';
import { useTranslation } from 'react-i18next';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { useDispatch, useSelector } from 'react-redux';

import TableStats from 'components/tableStats/TableStats';
import UserBadge from 'components/userBadge/UserBadge';
import UIButton from 'components/UIElements/UIButton';
import CustomerPurchasesContainer from 'components/customerPurchases/CustomerPurchasesContainer';
import {
  getProductFilterModuleEnabled,
  getUISetting,
} from 'reducers/configs/settings';
import { useBreakpoints } from 'utils/hooks/UI';
import { openModalPage } from 'actions/modalPage';
import { modalPages as mp, modalPages } from 'constants/modalPage';
import { getGridView, getGridIsOpened } from 'reducers/UI/gridDisplay';
import { PluginComponent } from 'plugins';
import { addProduct } from 'actions/ShoppingCart';
import {
  getCustomerRewardPointsSelector,
  getIsDefaultCustomer,
  getSelectedCustomer,
} from 'reducers/customerSearch';
import { getPreviousPurchases, getTotalVisitsToStore } from 'reducers/sales';

import './visibleCustomerInformation.css';

const titles = {
  storeCredit: 'storeCredit',
  rewardPoints: 'rewardPoints',
  visits: 'visits',
};

export const BadgeComponent = ({
  totalVisitsToStore,
  previousPurchases,
  customerRewardPoints,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('customerInformation');
  const currentCustomer = useSelector(getSelectedCustomer);

  const displayCustomerView = useCallback(
    () =>
      currentCustomer.id
        ? dispatch(
            openModalPage({
              component: mp.customerView,
              groupID: mp.customerView,
              replace: true,
            }),
          )
        : null,
    [currentCustomer.id, dispatch],
  );

  const gridDisplay = useSelector(getGridView);
  const largeView = useBreakpoints().mdplus;
  const addProductHandler = useCallback(prod => dispatch(addProduct(prod)), [
    dispatch,
  ]);

  // Check if Filter Products button should be rendered instead of Purchases
  const filterProductsEnabled = useSelector(getProductFilterModuleEnabled);
  const openFilterProductsView = useCallback(() => {
    dispatch(
      openModalPage({
        component: modalPages.productsFilter,
        groupID: modalPages.productsFilter,
        replace: true,
      }),
    );
  }, [dispatch]);

  const dataHeaders = useMemo(
    () =>
      Object.keys(titles).map(name => ({
        key: name,
        value: t(name).toUpperCase(),
      })),
    [t],
  );

  const data = useMemo(
    () => [
      { key: titles.storeCredit, value: currentCustomer.availableCredit },
      { key: titles.rewardPoints, value: customerRewardPoints },
      { key: titles.visits, value: totalVisitsToStore },
    ],
    [currentCustomer.availableCredit, customerRewardPoints, totalVisitsToStore],
  );

  return (
    <>
      <PluginComponent
        hookname="UICustomerBadgeCustomRegion"
        props={{
          totalVisitsToStore,
          previousPurchases,
          customerRewardPoints,
          currentCustomer,
        }}
      >
        <Row>
          {largeView && (
            <TableStats
              dataType="customer-bonus-info"
              dataHeaders={dataHeaders}
              data={data}
              isCustomer={true}
            />
          )}
        </Row>
        {largeView && (
          <Row className="user-badge-action-buttons">
            <Col>
              <UIButton
                text={t('details')}
                block={true}
                className="client-details-button"
                action={displayCustomerView}
              />
            </Col>
            <Col>
              {filterProductsEnabled ? (
                <UIButton
                  // todo filter button should be translated
                  text="FILTER"
                  block={true}
                  className="purchases-button"
                  action={openFilterProductsView}
                />
              ) : (
                <UIButton
                  text={t('purchases')}
                  block={true}
                  className="purchases-button"
                  action={displayCustomerView}
                />
              )}
            </Col>
          </Row>
        )}
        {!gridDisplay && largeView ? (
          <Col>
            <CustomerPurchasesContainer
              previousPurchases={previousPurchases}
              addProduct={addProductHandler}
            />
          </Col>
        ) : null}
      </PluginComponent>
    </>
  );
};

/** Component that displays general information about a client, like personal data, sales, rewards, etc. */
const VisibleCustomerInformation = () => {
  const hideCustomerInfo = useSelector(getUISetting('hideCustomerInfo'));
  const hideCustomerSearch = useSelector(getUISetting('hideCustomerSearch'));
  const currentCustomer = useSelector(getSelectedCustomer);
  const isDefaultCustomer = useSelector(getIsDefaultCustomer);
  const customerRewardPoints = useSelector(getCustomerRewardPointsSelector);
  const totalVisitsToStore = useSelector(getTotalVisitsToStore);
  const previousPurchases = useSelector(getPreviousPurchases);
  const gridDisplay = useSelector(getGridIsOpened);

  const largeView = useBreakpoints().mdplus;

  const currentCustomerName = useMemo(() => {
    if (Object.keys(currentCustomer ?? {}).length === 0) {
      return '';
    }
    return !currentCustomer.companyName || currentCustomer.companyName === ''
      ? `${currentCustomer.firstName} ${currentCustomer.lastName}`
      : currentCustomer.companyName;
  }, [currentCustomer]);

  if (hideCustomerInfo && largeView) {
    return null;
  }
  return (
    <PluginComponent
      hookname="UIVisibleCustomerInformation"
      props={{
        currentCustomerName,
        totalVisitsToStore,
        previousPurchases,
        customerRewardPoints,
        currentCustomer,
        isDefaultCustomer,
        largeView,
        gridDisplay,
      }}
    >
      <Container
        className="customer-information-container"
        data-testid="customer-information-container"
        style={{
          marginTop: largeView && 24,
          marginBottom:
            hideCustomerInfo && hideCustomerSearch && !largeView ? 0 : '16px',
          wordBreak: 'break-word',
        }}
      >
        <Row>
          <Col data-testid="user-column">
            <UserBadge
              customerName={currentCustomerName}
              isDefaultCustomer={isDefaultCustomer}
              customerCardNumber={currentCustomer.customerCardNumber}
              customerGroup={currentCustomer.groupName}
              isCustomer={true}
            />

            <BadgeComponent
              totalVisitsToStore={totalVisitsToStore}
              previousPurchases={previousPurchases}
              customerRewardPoints={customerRewardPoints}
              isCustomer={true}
            />
          </Col>
        </Row>
      </Container>
    </PluginComponent>
  );
};

export default VisibleCustomerInformation;
